import clsx from 'clsx';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
    readonly className?: string;
    readonly disabled?: boolean;
    readonly placeholder?: string;
    readonly variant?: 'default' | 'error' | 'success' | 'ghost' | 'search';
    readonly startElement?: ReactNode;
    readonly endElement?: ReactNode;
    readonly inputClassName?: string;
    /** `pill` matches the small pill buttons it sits beside, for search fields in page and tile title bars. */
    readonly shape?: 'default' | 'pill';
};

const variantSurfaceClasses = {
    default: 'bg-surface-raised',
    error: 'bg-surface-raised',
    success: 'bg-surface-raised',
    search: 'bg-surface-subtle',
    ghost: '',
} as const;

const variantClasses = {
    default: 'border-border-muted',
    error: 'border-border-error',
    success: 'border-border-success',
    search: 'border-transparent shadow-sm',
    ghost: '',
} as const;

const variantFocusClasses = {
    default:
        'hover:border-border-control-focus hover:surface-text-control-recessed focus:border-border-control-focus focus:surface-text-control-recessed',
    error: 'hover:border-border-error-strong focus:border-border-error-strong',
    success: 'hover:border-border-success-strong focus:border-border-success-strong',
    search: 'hover:surface-text-control-recessed focus:surface-text-control-recessed',
    ghost: '',
} as const;

const variantShellFocusClasses = {
    default:
        'hover:border-border-control-focus hover:surface-text-control-recessed focus-within:border-border-control-focus focus-within:surface-text-control-recessed',
    error: 'hover:border-border-error-strong focus-within:border-border-error-strong',
    success: 'hover:border-border-success-strong focus-within:border-border-success-strong',
    search: 'hover:surface-text-control-recessed focus-within:surface-text-control-recessed',
    ghost: '',
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    {
        className = '',
        disabled = false,
        placeholder,
        variant = 'default',
        startElement,
        endElement,
        inputClassName,
        shape = 'default',
        ...props
    },
    ref,
) {
    const isGhost = variant === 'ghost';
    const hasAdornment = (node: ReactNode) => node !== undefined && node !== null && node !== false;
    const isAdorned = !isGhost && (hasAdornment(startElement) || hasAdornment(endElement));

    const boxChrome = [
        variantSurfaceClasses[variant],
        'text-text-form',
        shape === 'pill' ? 'rounded-full border px-l py-0 h-control-sm' : 'rounded-md border px-l py-m',
        'transition-all duration-100 ease-in-out',
        variantClasses[variant],
    ];

    if (isAdorned) {
        return (
            <div
                className={clsx(
                    'box-border flex items-center gap-m',
                    boxChrome,
                    disabled ? 'cursor-default opacity-50' : variantShellFocusClasses[variant],
                    className,
                )}
            >
                {startElement}
                <input
                    ref={ref}
                    className={clsx(
                        'flex-1 min-w-0 bg-transparent border-none outline-none p-0',
                        'font-sans font-normal tracking-tight',
                        inputClassName ?? 'text-meta text-text-form placeholder:text-text-placeholder',
                    )}
                    disabled={disabled}
                    placeholder={placeholder}
                    {...props}
                />
                {endElement}
            </div>
        );
    }

    return (
        <input
            ref={ref}
            className={clsx(
                'w-full box-border outline-none font-sans',
                isGhost
                    ? 'border-none bg-transparent p-0'
                    : [
                          ...boxChrome,
                          'text-meta font-normal tracking-tight',
                          'placeholder:text-text-placeholder',
                          'file:bg-transparent file:border-none file:rounded-none file:text-inherit file:cursor-pointer file:font-inherit file:m-0 file:p-0 file:pr-l',
                      ],
                disabled ? 'cursor-default opacity-50' : clsx('cursor-text', variantFocusClasses[variant]),
                className,
            )}
            disabled={disabled}
            placeholder={placeholder}
            {...props}
        />
    );
});
